import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import publicacaoService from "../services/publicacao";
import { statusLabels } from "../utils/constants";
import { formatDateToShow } from "../utils/formatters";
import { getApiErrorMessage } from "../utils/getApiErrorMessage";
import {
  BUSCA_PUBLICA_HELPER,
  isBuscaPublicaElegivel,
} from "../utils/publicacaoBusca";

export default function Publicacoes() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [tiposProcesso, setTiposProcesso] = useState([]);
  const [busca, setBusca] = useState("");
  const [buscaDebounced, setBuscaDebounced] = useState("");
  const [tipoLicencaId, setTipoLicencaId] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const buscaIncompleta =
    Boolean(busca.trim()) && !isBuscaPublicaElegivel(busca);

  useEffect(() => {
    const timer = setTimeout(() => setBuscaDebounced(busca.trim()), 400);
    return () => clearTimeout(timer);
  }, [busca]);

  useEffect(() => {
    publicacaoService
      .listarTiposProcesso()
      .then(setTiposProcesso)
      .catch((err) => {
        if (err?.status === 429) setError(err.message);
      });
  }, []);

  const carregar = useCallback(async () => {
    if (!isBuscaPublicaElegivel(buscaDebounced)) {
      setLoading(false);
      setError("");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const result = await publicacaoService.listarPublicas({
        busca: buscaDebounced,
        tipoLicencaId: tipoLicencaId || null,
        pageNumber: page + 1,
        pageSize: rowsPerPage,
      });
      setItems(result.data || []);
      setTotalCount(result.totalCount || 0);
    } catch (err) {
      setItems([]);
      setTotalCount(0);
      setError(getApiErrorMessage(err, "Erro ao carregar publicações."));
    } finally {
      setLoading(false);
    }
  }, [buscaDebounced, tipoLicencaId, page, rowsPerPage]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 1, fontWeight: 600 }}>
        Publicações
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Consulte as publicações de licenças ambientais no site do Licenciar.
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
          }}
        >
          <TextField
            label="Buscar por nome ou CPF/CNPJ"
            value={busca}
            onChange={(e) => {
              setPage(0);
              setBusca(e.target.value);
            }}
            fullWidth
            size="small"
            helperText={buscaIncompleta ? BUSCA_PUBLICA_HELPER : " "}
            FormHelperTextProps={{
              sx: { minHeight: "1.25em" },
            }}
          />
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel id="tipo-processo-label">Tipo de processo</InputLabel>
            <Select
              labelId="tipo-processo-label"
              label="Tipo de processo"
              value={tipoLicencaId}
              onChange={(e) => {
                setPage(0);
                setTipoLicencaId(e.target.value);
              }}
            >
              <MenuItem value="">Todos</MenuItem>
              {tiposProcesso.map((tipo) => (
                <MenuItem key={tipo.id} value={tipo.id}>
                  {tipo.nome}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Empreendimento</TableCell>
                  <TableCell>CPF/CNPJ</TableCell>
                  <TableCell>Data de abertura</TableCell>
                  <TableCell>Situação</TableCell>
                  <TableCell>Tipo de processo</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Nenhuma publicação encontrada.
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{item.empreendimentoNome}</TableCell>
                      <TableCell>{item.empreendimentoCnpjCpf}</TableCell>
                      <TableCell>{formatDateToShow(item.dataAberturaSolicitacao)}</TableCell>
                      <TableCell>
                        {statusLabels[item.statusSolicitacao] ?? item.statusSolicitacao}
                      </TableCell>
                      <TableCell>{item.tipoLicencaNome}</TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => navigate(`/publicacoes/${item.id}`)}
                        >
                          Detalhes
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage="Linhas"
            />
          </>
        )}
      </TableContainer>
    </Container>
  );
}
